import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Settings as Lungs, BarChart, ArrowRight, Wind, Heart, Brain, AlertCircle, Zap, Download } from 'lucide-react';

const EmissionCalculatorPreview: React.FC = () => {
  const [motorcycleType, setMotorcycleType] = useState<string>('medium');
  const [kmPerDay, setKmPerDay] = useState<number>(30);

  const motorcycleTypes = [
    { id: 'small', name: 'Petite cylindrée', co2: '60g/km' },
    { id: 'medium', name: 'Moyenne cylindrée', co2: '80g/km' },
    { id: 'large', name: 'Grande cylindrée', co2: '110g/km' }
  ];

  const healthEffects = [
    {
      icon: <Lungs size={24} className="text-red-500" />,
      title: "Problèmes Respiratoires",
      description: "Risques accrus d'asthme et de maladies respiratoires"
    },
    {
      icon: <Heart size={24} className="text-red-500" />,
      title: "Maladies Cardiovasculaires",
      description: "Augmentation des risques cardiaques"
    },
    {
      icon: <Brain size={24} className="text-red-500" />,
      title: "Impact Neurologique",
      description: "Effets sur le développement cognitif"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(24,153,78,0.05)_0%,_transparent_50%)]"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 mb-4 sm:mb-5 md:mb-6 bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
            Calculateur d'Émissions CO₂
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed px-4">
            Évaluez l'impact environnemental et sanitaire de votre moto thermique, et découvrez les avantages d'une transition vers l'électrique.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
          {/* Preview of Calculator */}
          <div className="bg-white rounded-xl shadow-xl p-5 sm:p-6 md:p-8 border border-primary-100 transform transition-all duration-500 hover:shadow-2xl hover:border-primary-200">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-5 md:mb-6 flex items-center">
              <BarChart size={24} className="text-primary-600 mr-2" />
              Simulez Votre Impact
            </h3>
            
            <div className="space-y-4 sm:space-y-5 md:space-y-6 mb-6 sm:mb-7 md:mb-8">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Type de Moto
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {motorcycleTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setMotorcycleType(type.id)}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        motorcycleType === type.id
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-primary-200 text-gray-600'
                      }`}
                    >
                      {type.name}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Distance parcourue par jour
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="5"
                    max="100"
                    step="5"
                    value={kmPerDay}
                    onChange={(e) => setKmPerDay(parseInt(e.target.value))}
                    className="w-full accent-primary-500"
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>5 km</span>
                    <span className="font-medium text-primary-600">{kmPerDay} km</span>
                    <span>100 km</span>
                  </div>
                </div>
              </div>
            </div>
            
            <Link
              to="/emission-calculator"
              className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center"
            >
              Calculer mon impact complet
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </div>
          
          {/* Impact Preview */}
          <div className="space-y-6">
            <div className="bg-primary-50 rounded-xl p-6 border border-primary-100 transform transition-all duration-500 hover:shadow-lg hover:border-primary-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Leaf size={24} className="text-primary-600 mr-2" />
                Impact Environnemental
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <Wind size={20} className="text-primary-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Émissions de CO₂</p>
                    <p className="text-gray-600 text-sm">Une moto thermique émet en moyenne 80g de CO₂ par kilomètre</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <AlertCircle size={20} className="text-primary-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Pollution Atmosphérique</p>
                    <p className="text-gray-600 text-sm">Les motos contribuent jusqu'à 30% de la pollution urbaine dans certaines zones</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-red-50 rounded-xl p-6 border border-red-100 transform transition-all duration-500 hover:shadow-lg hover:border-red-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Lungs size={24} className="text-red-500 mr-2" />
                Impact sur la Santé
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {healthEffects.map((effect, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-center mb-2">
                      {effect.icon}
                      <h4 className="font-medium text-gray-900 ml-2">{effect.title}</h4>
                    </div>
                    <p className="text-gray-600 text-sm">{effect.description}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-green-50 rounded-xl p-6 border border-green-100 transform transition-all duration-500 hover:shadow-lg hover:border-green-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Zap size={24} className="text-green-600 mr-2" />
                Solution: Passez à l'Électrique
              </h3>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Zéro émission directe</p>
                  <p className="text-gray-600 text-sm mb-4">Réduisez votre empreinte carbone à zéro</p>
                  
                  <Link
                    to="/emission-calculator"
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Calculer mes économies
                    <ArrowRight size={16} className="ml-1" />
                  </Link>
                </div>
                
                <Download size={24} className="text-primary-600" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmissionCalculatorPreview;